import _ from 'lodash'
import { makeStyles } from '@material-ui/core'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { useActionRunner, useNotification } from 'src/modules/notification'
import { Container, fileToBase64, PageTitle } from '../../../../../ui'
import { usePurchasing } from '../../../purchasing'

import { useRecipes } from '../../hooks'

import RecipeForm from '../forms/RecipeForm'
import RecipeSummarySidePanel from '../RecipeSummarySidePanel'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
    },
  },
  content: {
    flexGrow: 1,
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  buttonLink: {
    textDecoration: 'none',
  },
}))

const CreateRecipeFormView = () => {
  const {
    sections,
    jobTypes,
    fetchJobTypes,
    fetchEquipmentTypes,
    equipmentTypes,
    createRecipeIngredient,
    createBaseRecipe,
    fetchCategories,
    categories,
    createRecipeStepJobType,
    createRecipeStepEquipmentType,
    createRecipeStep,
    createRecipePhoto,
    createStepPhoto,
  } = useRecipes()
  const { fetchProducts, purchasingEnvironment } = usePurchasing()
  const { showError } = useNotification()

  let formData

  const history = useHistory()
  const classes = useStyles()

  const [fetchData, isFetching] = useActionRunner(
    [
      () => fetchProducts(purchasingEnvironment),
      () => fetchJobTypes(purchasingEnvironment),
      () => fetchCategories(purchasingEnvironment),
      () => fetchEquipmentTypes(purchasingEnvironment),
    ],
    {
      initialFetchingState: true,
    }
  )

  useEffect(() => {
    fetchData()
  }, [])

  const recipeFormRef = useRef(null)

  if (isFetching) {
    return null
  }

  // TODO: Move this to redux - hack for time contraints
  const submitRecipeFile = async (recipeId, base64Data) => {
    const formData = new FormData()
    formData.append('photo', base64Data)
    return await createRecipePhoto({ recipeId, formData })
  }

  const submitStepFile = async (stepId, base64Data) => {
    const formData = new FormData()
    formData.append('photo', base64Data)
    return await createStepPhoto({ stepId, formData })
  }

  const handleSubmit = async (values, { setErrors }) => {
    const summaryFormValues = values

    const recipeFormState = recipeFormRef.current.getState()
    const state = { summary: summaryFormValues, recipeForm: recipeFormState }

    try {
      const wrappedRecipeResult = await createBaseRecipe({
        name: state.recipeForm.values.name,
        section: state.summary.section,
        categories: [...state.summary.category],
        recipe_type: state.summary.batch,
        batch_size: state.summary.batchSize ?? null,
        batch_portions: state.summary.batchPortions ?? null,
        menu_price: state.summary.menuPrice ?? null,
        unit: state.summary.unitSelect ?? 'g',
      })

      const recipe = unwrapResult(wrappedRecipeResult)

      if (values.recipePhoto) {
        const data = await fileToBase64(values.recipePhoto)
        if (data) {
          await submitRecipeFile(recipe.id, data)
        }
      }

      // Loop Ingredients
      await Promise.all(
        state.recipeForm.selectedIngredients.map(async i => {
          const ingredient = {
            recipe_id: recipe.id,
            ingredient_id: i.ingredientId,
            ingredient_type: i.ingredientType,
            ingredient_amount: i.quantity,
          }
          return await createRecipeIngredient(ingredient)
        })
      )

      await Promise.all(
        state.recipeForm.stepFormRefs.current.map(
          async (stepFormRef, index) => {
            const stepState = stepFormRef.getState().values

            const wrappedStep = await createRecipeStep({
              description: stepState.step,
              recipe_id: recipe.id,
              position: index,
            })
            const step = unwrapResult(wrappedStep)

            if (stepState.jobType && stepState.jobTypeTime) {
              const jobType = {
                step: step.id,
                job_type: stepState.jobType,
                time_in_mins: stepState.jobTypeTime,
              }
              await createRecipeStepJobType(jobType)
            }

            if (stepState.equipmentType && stepState.equipmentTypeTime) {
              const equipmentType = {
                step: step.id,
                equipment: stepState.equipmentType,
                time_in_mins: stepState.equipmentTypeTime,
              }
              await createRecipeStepEquipmentType(equipmentType)
            }

            if (stepState.stepPhoto) {
              const data = await fileToBase64(stepState.stepPhoto)
              if (data) {
                await submitStepFile(step.id, data)
              }
            }

            return step
          }
        )
      )

      history.push(`/recipes/`)
      return recipe
    } catch (error) {
      let errorMessage = ``
      let errors = error.data

      if (!_.isEmpty(errors)) {
        for (const [key, value] of Object.entries(errors)) {
          errorMessage = errorMessage + `\n ${key} - ${value}`
        }
      }
      showError(errorMessage)
    }
  }

  return (
    <Container>
      <PageTitle>Create a new Recipe</PageTitle>
      <div className={classes.body}>
        <div className={classes.content}>
          <RecipeForm
            jobTypes={jobTypes}
            equipment={equipmentTypes}
            title="Create Recipe"
            ref={recipeFormRef}
          />
        </div>
        <RecipeSummarySidePanel
          courses={sections}
          categories={categories}
          formData={formData}
          onSubmit={handleSubmit}
        />
      </div>
      <></>
    </Container>
  )
}

export default CreateRecipeFormView
